import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid2,
    Card,
    CardContent,
    Chip,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    History as HistoryIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    HourglassEmpty as PendingIcon,
    Analytics as AnalyticsIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { ethers } from 'ethers';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import EnhancedTransactionHistory from './EnhancedTransactionHistory';
import SecureDataViewer from '../common/SecureDataViewer';
import TransactionStatus from '../common/TransactionStatus';
import logger from '../../utils/logger';

interface TrackedTransaction {
    hash: string;
    from: string;
    to: string;
    value: string;
    timestamp: number;
    status: 'pending' | 'success' | 'error';
    gasUsed?: string;
    gasCost?: string;
    gasLimit?: string;
    error?: string;
    data?: string;
    description?: string;
    internalTransactions?: any[];
    decodedData?: {
        contract: string;
        method: string;
        args: any;
        signature: string;
    } | null;
    relatedOpHash?: string;
}

const TransactionHistory: React.FC = () => {
    const { provider, signer, executionStatus, setExecutionStatus } = useWallet();
    const { contracts } = useContracts();

    // State variables
    const [transactions, setTransactions] = useState<TrackedTransaction[]>([]);
    const [relatedTxs, setRelatedTxs] = useState<Map<string, string>>(new Map());
    const [trackedOperations, setTrackedOperations] = useState<{
        id: string;
        target: string;
        status: 'scheduled' | 'executable' | 'executed';
        scheduledTx: string;
        executedTx?: string;
        executionTime: number;
    }[]>([]);
    const [transactionToAnalyze, setTransactionToAnalyze] = useState('');
    const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
    const [analyzedTransaction, setAnalyzedTransaction] = useState<any>(null);

    // Load saved transactions on mount
    useEffect(() => {
        const savedTxs = localStorage.getItem('memer_transactions');
        if (savedTxs) {
            try {
                setTransactions(JSON.parse(savedTxs));
            } catch (error) {
                logger.error("Error parsing saved transactions:", error);
            }
        }

        // Load operation relationships
        const savedOps = localStorage.getItem('memer_operations');
        if (savedOps) {
            try {
                const opEntries = JSON.parse(savedOps);
                setRelatedTxs(new Map(opEntries));
            } catch (error) {
                logger.error("Error parsing saved operations:", error);
            }
        }

        // Load tracked operations
        const savedOperations = localStorage.getItem('memer_tracked_operations');
        if (savedOperations) {
            try {
                setTrackedOperations(JSON.parse(savedOperations));
            } catch (error) {
                logger.error("Error parsing saved operations:", error);
            }
        }
    }, []);

    // Save transactions when they change
    useEffect(() => {
        if (transactions.length > 0) {
            localStorage.setItem('memer_transactions', JSON.stringify(transactions));
        }
    }, [transactions]);

    // Save related operations when they change
    useEffect(() => {
        if (relatedTxs.size > 0) {
            localStorage.setItem('memer_operations',
                JSON.stringify(Array.from(relatedTxs.entries())));
        }
    }, [relatedTxs]);

    // Save tracked operations when they change
    useEffect(() => {
        if (trackedOperations.length > 0) {
            localStorage.setItem('memer_tracked_operations', JSON.stringify(trackedOperations));
        }
    }, [trackedOperations]);

    // Utility functions
    const isTimelockSchedule = (tx: ethers.providers.TransactionResponse): boolean => {
        try {
            // Find the timelock contract
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));
            if (!timelockContract) return false;

            // Check if transaction is to timelock contract
            if (tx.to?.toLowerCase() !== timelockContract.address.toLowerCase()) return false;

            // Create interface to decode function call
            const iface = new ethers.utils.Interface(timelockContract.abi);
            const decoded = iface.parseTransaction({ data: tx.data || '0x' });

            // Check if this is a schedule or scheduleBatch call
            return decoded.name === 'schedule' || decoded.name === 'scheduleBatch';
        } catch {
            return false;
        }
    };

    const getOperationHash = async (data: string): Promise<string> => {
        try {
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));
            if (!timelockContract || !provider) return '';

            const timelock = new ethers.Contract(
                timelockContract.address,
                timelockContract.abi,
                provider
            );

            // Decode the schedule call parameters
            const iface = new ethers.utils.Interface(timelockContract.abi);
            const decoded = iface.parseTransaction({ data });

            // Get operation hash from parameters
            if (decoded.name === 'schedule') {
                return await timelock.hashOperation(
                    decoded.args.target,
                    decoded.args.value,
                    decoded.args.data,
                    decoded.args.predecessor,
                    decoded.args.salt
                );
            }
            return '';
        } catch (error) {
            logger.error("Error getting operation hash:", error);
            return '';
        }
    };

    // Function to log blockchain transactions
    const logBlockchainTransaction = async (
        transaction: ethers.providers.TransactionResponse,
        setStatus?: (status: string) => void
    ): Promise<ethers.providers.TransactionReceipt | null> => {
        try {
            // Create a new tracked transaction
            const newTransaction: TrackedTransaction = {
                hash: transaction.hash,
                from: transaction.from,
                to: transaction.to || '',
                value: transaction.value.toString(),
                timestamp: Date.now(),
                status: 'pending',
                gasLimit: transaction.gasLimit.toString(),
                data: transaction.data
            };

            // Add to transaction history
            setTransactions((prev: TrackedTransaction[]) => [newTransaction, ...prev].slice(0, 10));

            // Log initial transaction details
            logger.log("Transaction Initiated:", {
                hash: transaction.hash,
                from: transaction.from,
                to: transaction.to,
                value: transaction.value.toString(),
                nonce: transaction.nonce,
                gasLimit: transaction.gasLimit.toString(),
                gasPrice: transaction.gasPrice?.toString() || 'unknown'
            });

            // Update UI if available
            if (setStatus) {
                setStatus(`Transaction initiated. Hash: ${transaction.hash}`);
            }

            // Wait for confirmation and log progress
            let receipt: ethers.providers.TransactionReceipt | null = null;
            try {
                receipt = await transaction.wait(1); // Wait for 1 confirmation
            } catch (waitError) {
                logger.error("Error waiting for transaction receipt:", waitError);

                // Update transaction status to error
                setTransactions((prev: TrackedTransaction[]) =>
                    prev.map(tx =>
                        tx.hash === transaction.hash
                            ? {
                                ...tx,
                                status: 'error',
                                error: waitError instanceof Error ? waitError.message : 'Transaction failed'
                            }
                            : tx
                    )
                );

                return null;
            }

            // Ensure receipt exists before processing
            if (!receipt) {
                logger.error("No receipt returned for transaction");
                return null;
            }

            // Calculate gas used and cost
            const gasCost = receipt.gasUsed.mul(transaction.gasPrice || 0);
            const formattedGasCost = ethers.utils.formatEther(gasCost);

            // Update transaction in history
            setTransactions((prev: TrackedTransaction[]) =>
                prev.map(tx =>
                    tx.hash === transaction.hash
                        ? {
                            ...tx,
                            status: receipt!.status === 1 ? 'success' : 'error',
                            gasUsed: receipt!.gasUsed.toString(),
                            gasCost: formattedGasCost
                        }
                        : tx
                )
            );

            // Try to analyze internal transactions if contracts are available
            let internalTransactions: any[] = [];
            try {
                internalTransactions = await analyzeTransactionReceipt(receipt, contracts);
            } catch (analysisError) {
                logger.error("Error analyzing transaction receipt:", analysisError);
            }

            // Log detailed completion information
            logger.log("Transaction Completed:", {
                status: receipt.status === 1 ? 'Success' : 'Failed',
                transactionHash: receipt.transactionHash,
                blockNumber: receipt.blockNumber,
                gasUsed: receipt.gasUsed.toString(),
                cumulativeGasUsed: receipt.cumulativeGasUsed.toString(),
                gasCostETH: formattedGasCost,
                events: receipt.logs.length > 0 ? `${receipt.logs.length} events emitted` : 'No events'
            });

            // Update UI with completion status
            if (setStatus) {
                setStatus(
                    `Transaction completed successfully. Hash: ${receipt.transactionHash}. ` +
                    `Gas used: ${receipt.gasUsed.toString()}. Cost: ${formattedGasCost} ETH.`
                );
            }

            return receipt;
        } catch (error: any) {
            // Update transaction in history with error
            setTransactions((prev: TrackedTransaction[]) =>
                prev.map(tx =>
                    tx.hash === transaction.hash
                        ? {
                            ...tx,
                            status: 'error',
                            error: error.message || 'Unknown error'
                        }
                        : tx
                )
            );

            // Detailed error logging
            logger.error("Transaction Error:", {
                hash: transaction.hash,
                error: error.message,
                code: error.code,
                reason: error.reason || 'Unknown reason',
                method: error.method,
                transaction: error.transaction,
            });

            // Check for common errors and provide better user messages
            let errorMessage = "Transaction failed";

            if (error.code === 'INSUFFICIENT_FUNDS') {
                errorMessage = "Transaction failed: Insufficient funds for gas";
            } else if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
                errorMessage = "Transaction failed: Contract execution error";
            } else if (error.message.includes('user rejected')) {
                errorMessage = "Transaction cancelled by user";
            } else if (error.message.includes('nonce')) {
                errorMessage = "Transaction failed: Nonce error. Try refreshing your page";
            }

            // Update UI with error
            if (setStatus) {
                setStatus(`${errorMessage}. Hash: ${transaction.hash}`);
            }

            return null;
        }
    };

    async function analyzeTransactionReceipt(
        receipt: ethers.providers.TransactionReceipt,
        contracts: { name: string; address: string; abi: any }[]
    ): Promise<any[]> {
        const internalTransactions = [];

        try {
            // Process the logs to identify events from our known contracts
            for (const log of receipt.logs) {
                // Find the contract that emitted this log
                const matchingContract = contracts.find(c =>
                    c.address.toLowerCase() === log.address.toLowerCase()
                );

                if (matchingContract) {
                    try {
                        // Create an interface to parse the log
                        const contractInterface = new ethers.utils.Interface(matchingContract.abi);

                        // Try to parse the log
                        const parsedLog = contractInterface.parseLog(log);

                        if (parsedLog) {
                            // Extract relevant information from the event
                            const eventInfo = {
                                hash: log.transactionHash,
                                logIndex: log.logIndex,
                                contract: matchingContract.name,
                                contractAddress: log.address,
                                eventName: parsedLog.name,
                                eventSignature: parsedLog.signature,
                                args: parsedLog.args,
                                description: `${matchingContract.name}.${parsedLog.name} event`,
                                from: '',
                                to: '',
                                value: '' // Add the value property
                            };

                            // Special handling for specific events
                            if (parsedLog.name === 'CallExecuted') {
                                // This is from the Timelock's execute function
                                eventInfo.description = `Timelock executed call to ${parsedLog.args.target}`;
                                eventInfo.to = parsedLog.args.target;
                                if (parsedLog.args.value !== undefined) {
                                    eventInfo.value = parsedLog.args.value.toString();
                                }

                                // If the target is a known contract, identify the function
                                const targetContract = contracts.find(c =>
                                    c.address.toLowerCase() === parsedLog.args.target.toLowerCase()
                                );

                                if (targetContract) {
                                    const targetInterface = new ethers.utils.Interface(targetContract.abi);
                                    try {
                                        const funcFragment = targetInterface.getFunction(
                                            parsedLog.args.data.slice(0, 10)
                                        );
                                        if (funcFragment) {
                                            eventInfo.description = `Timelock executed ${targetContract.name}.${funcFragment.name}`;
                                        }
                                    } catch (e) {
                                        // Function signature not found, continue with generic description
                                    }
                                }
                            }
                            else if (parsedLog.name === 'Transfer') {
                                // This is likely an ERC20 transfer
                                eventInfo.description = `Token transfer from ${parsedLog.args.from.substring(0, 6)}...${parsedLog.args.from.substring(parsedLog.args.from.length - 4)
                                    } to ${parsedLog.args.to.substring(0, 6)}...${parsedLog.args.to.substring(parsedLog.args.to.length - 4)}`;
                                eventInfo.from = parsedLog.args.from;
                                eventInfo.to = parsedLog.args.to;
                                eventInfo.value = parsedLog.args.value?.toString() || '0';
                            }

                            internalTransactions.push(eventInfo);
                        }
                    } catch (parseError) {
                        logger.warn("Error parsing log:", parseError);
                        // Add as unknown event
                        internalTransactions.push({
                            hash: log.transactionHash,
                            logIndex: log.logIndex,
                            contract: matchingContract.name,
                            contractAddress: log.address,
                            description: `Unknown event from ${matchingContract.name}`
                        });
                    }
                }
            }
        } catch (error) {
            logger.error("Error analyzing transaction receipt:", error);
        }

        return internalTransactions;
    }

    // Helper function to decode transaction data
    function decodeTransactionData(data: string, contracts: { name: string; address: string; abi: any }[]): any {
        if (!data || data === '0x') return null;

        // Try to decode the function call using ABIs of all known contracts
        for (const contract of contracts) {
            try {
                const contractInterface = new ethers.utils.Interface(contract.abi);
                const decoded = contractInterface.parseTransaction({ data });

                if (decoded) {
                    return {
                        contract: contract.name,
                        method: decoded.name,
                        args: decoded.args,
                        signature: decoded.signature
                    };
                }
            } catch (error) {
                // This contract's ABI doesn't match the function signature, try the next one
                continue;
            }
        }

        return null; // Unable to decode with any known ABI
    }

    // Custom error signatures for common errors
    const ERROR_SIGNATURES = {
        // MemerCore errors
        '0x609e62f4': {
            name: 'MemerCore__AllowanceExceeded',
            params: ['address', 'uint256', 'uint256'],
            description: 'The spender does not have enough allowance to transfer tokens on behalf of the owner',
            solution: 'The owner needs to approve the spender to transfer tokens on their behalf'
        },
        '0x01336cea': {
            name: 'MemerCore__InsufficientBalance',
            params: ['address', 'uint256', 'uint256'],
            description: 'The sender does not have enough tokens to transfer',
            solution: 'The sender needs to acquire more tokens before attempting the transfer'
        },
        '0xea8e4eb5': {
            name: 'MemerCore__TransferToZeroAddress',
            params: [],
            description: 'Cannot transfer tokens to the zero address',
            solution: 'Provide a valid recipient address'
        },
        // Add more error signatures as needed
    };

    // Helper function to decode custom errors
    const decodeCustomError = (data: string): { name: string; args: any[]; description: string; solution: string } | null => {
        if (!data || data.length < 10) return null;

        const errorSelector = data.slice(0, 10);
        const errorInfo = ERROR_SIGNATURES[errorSelector as keyof typeof ERROR_SIGNATURES];

        if (!errorInfo) return null;

        try {
            // Create a custom error interface
            const errorInterface = new ethers.utils.Interface([
                `error ${errorInfo.name}(${errorInfo.params.join(',')})`
            ]);

            // Decode the error data
            const decodedError = errorInterface.parseError(data);

            return {
                name: errorInfo.name,
                args: Array.from(decodedError.args),
                description: errorInfo.description,
                solution: errorInfo.solution
            };
        } catch (e) {
            logger.error("Error decoding custom error:", e);
            return {
                name: errorInfo.name,
                args: [],
                description: errorInfo.description,
                solution: errorInfo.solution
            };
        }
    };

    // Helper function to trace a call and its result
    const traceCall = async (
        contract: { name: string; address: string; abi: any },
        functionName: string,
        args: any[],
        callData: string,
        statusText: string,
        provider: ethers.providers.Provider
    ): Promise<{ statusText: string; error?: { data: string; decoded: any } }> => {
        let result: { statusText: string; error?: { data: string; decoded: any } } = { statusText };

        try {
            // Create contract instance
            const contractInstance = new ethers.Contract(
                contract.address,
                contract.abi,
                provider
            );

            // Try to simulate the call to see if it would succeed
            try {
                const callResult = await provider.call({
                    to: contract.address,
                    data: callData
                });

                result.statusText += `\n${contract.name}.${functionName} would succeed`;
            } catch (callError: any) {
                // Extract error data from revert
                const errorData = callError.data ||
                    (callError.error && callError.error.data) ||
                    (typeof callError === 'object' && 'data' in callError ? callError.data : null);

                if (errorData) {
                    // Try to decode the error
                    const decodedError = decodeCustomError(errorData);

                    if (decodedError) {
                        result.statusText += `\n${contract.name}.${functionName} would fail with error: ${decodedError.name}`;
                        result.statusText += `\nError description: ${decodedError.description}`;
                        result.statusText += `\nSolution: ${decodedError.solution}`;

                        // Format error arguments
                        if (decodedError.args && decodedError.args.length > 0) {
                            result.statusText += `\nError details:`;
                            decodedError.args.forEach((arg, index) => {
                                const paramName = decodedError.name.includes('Allowance') && index === 0 ? 'Owner' :
                                    decodedError.name.includes('Allowance') && index === 1 ? 'Current allowance' :
                                        decodedError.name.includes('Allowance') && index === 2 ? 'Required allowance' :
                                            `Param ${index}`;

                                const formattedValue = ethers.BigNumber.isBigNumber(arg) ?
                                    ethers.utils.formatUnits(arg, 18) :
                                    String(arg);

                                result.statusText += `\n  - ${paramName}: ${formattedValue}`;
                            });
                        }

                        result.error = { data: errorData, decoded: decodedError };
                    } else {
                        result.statusText += `\n${contract.name}.${functionName} would fail with unknown error`;
                        result.error = { data: errorData, decoded: null };
                    }
                } else {
                    result.statusText += `\n${contract.name}.${functionName} would fail`;
                }
            }
        } catch (e) {
            logger.error(`Error tracing call to ${contract.name}.${functionName}:`, e);
            result.statusText += `\nError analyzing call to ${contract.name}.${functionName}`;
        }

        return result;
    };

    const traceTransactionChain = async (txHash: string) => {
        if (!provider) {
            setExecutionStatus("Provider not initialized");
            return;
        }

        try {
            setExecutionStatus(`Tracing transaction chain from ${txHash}...`);

            // Get the starting transaction
            const startTx = await provider.getTransaction(txHash);
            if (!startTx) {
                setExecutionStatus("Transaction not found");
                return;
            }

            // Get transaction receipt
            const receipt = await provider.getTransactionReceipt(txHash);

            // Analyze the transaction based on the target contract
            const targetContract = contracts.find(c =>
                c.address.toLowerCase() === (startTx.to || '').toLowerCase()
            );

            if (!targetContract) {
                setExecutionStatus(`Transaction target ${startTx.to} not found in known contracts`);
                return;
            }

            setExecutionStatus(`Transaction is targeting ${targetContract.name}`);
            let statusText = `Transaction is targeting ${targetContract.name}`;

            // Try to decode the function call
            try {
                const contractInterface = new ethers.utils.Interface(targetContract.abi);
                const decoded = contractInterface.parseTransaction({ data: startTx.data });

                if (decoded) {
                    statusText += `\nCalling ${targetContract.name}.${decoded.name}`;
                    setExecutionStatus(statusText);

                    // Special handling for multisig execTransaction
                    if (targetContract.name.toLowerCase().includes("multisig") && decoded.name === 'execTransaction') {
                        // This is a MultiSig execTransaction call
                        const target = decoded.args.to;
                        const payload = decoded.args.data;

                        // Find the target contract of the nested call
                        const nestedTargetContract = contracts.find(c =>
                            c.address.toLowerCase() === target.toLowerCase()
                        );

                        if (nestedTargetContract) {
                            statusText += `\nMultiSig is calling contract: ${nestedTargetContract.name}`;
                            setExecutionStatus(statusText);

                            // Try to decode the nested call
                            try {
                                const nestedInterface = new ethers.utils.Interface(nestedTargetContract.abi);
                                const nestedCall = nestedInterface.parseTransaction({ data: payload });

                                if (nestedCall) {
                                    statusText += `\nNested call: ${nestedTargetContract.name}.${nestedCall.name}`;
                                    setExecutionStatus(statusText);

                                    // If this is calling the timelock
                                    if (nestedTargetContract.name.toLowerCase().includes("timelock")) {
                                        if (nestedCall.name === 'schedule') {
                                            // Extract the schedule parameters
                                            const timelockTarget = nestedCall.args.target;
                                            const timelockData = nestedCall.args.data;
                                            const delay = nestedCall.args.delay;

                                            // Find the target of the timelock call
                                            const timelockTargetContract = contracts.find(c =>
                                                c.address.toLowerCase() === timelockTarget.toLowerCase()
                                            );

                                            if (timelockTargetContract) {
                                                statusText += `\nTimelock will call: ${timelockTargetContract.name}`;
                                                setExecutionStatus(statusText);

                                                // Try to decode the timelock's target function
                                                try {
                                                    const finalInterface = new ethers.utils.Interface(timelockTargetContract.abi);
                                                    const finalCall = finalInterface.parseTransaction({ data: timelockData });

                                                    if (finalCall) {
                                                        statusText += `\nFinal call will be: ${timelockTargetContract.name}.${finalCall.name}`;
                                                        setExecutionStatus(statusText);

                                                        // If this is a transfer, show details
                                                        if (finalCall.name === 'transfer') {
                                                            // Log the full args object to debug
                                                            logger.log("Transfer function args:", finalCall.args);

                                                            // More robust parameter extraction
                                                            let recipient;
                                                            let amount;

                                                            // Handle different ways parameters might be structured
                                                            if (finalCall.args) {
                                                                if (typeof finalCall.args === 'object') {
                                                                    // Named parameters
                                                                    if ('to' in finalCall.args) {
                                                                        recipient = finalCall.args.to;
                                                                    } else if (finalCall.args[0] !== undefined) {
                                                                        recipient = finalCall.args[0];
                                                                    }

                                                                    // Try different possible names for amount
                                                                    if ('value' in finalCall.args) {
                                                                        amount = finalCall.args.value;
                                                                    } else if ('amount' in finalCall.args) {
                                                                        amount = finalCall.args.amount;
                                                                    } else if (finalCall.args[1] !== undefined) {
                                                                        amount = finalCall.args[1];
                                                                    }
                                                                } else if (Array.isArray(finalCall.args)) {
                                                                    // Array parameters
                                                                    recipient = finalCall.args[0];
                                                                    amount = finalCall.args[1];
                                                                }
                                                            }

                                                            if (recipient) {
                                                                statusText += `\nTransfer recipient: ${recipient}`;
                                                                setExecutionStatus(statusText);
                                                            } else {
                                                                statusText += `\nTransfer recipient: Could not determine recipient`;
                                                                setExecutionStatus(statusText);
                                                            }

                                                            if (amount) {
                                                                try {
                                                                    const formattedAmount = ethers.utils.formatUnits(amount, 18);
                                                                    statusText += `\nTransfer amount: ${formattedAmount} MEMER`;
                                                                    setExecutionStatus(statusText);
                                                                } catch (e) {
                                                                    logger.error("Error formatting amount:", e);
                                                                    statusText += `\nTransfer amount: ${String(amount)}`;
                                                                    setExecutionStatus(statusText);
                                                                }
                                                            } else {
                                                                statusText += `\nTransfer amount: Could not determine amount`;
                                                                setExecutionStatus(statusText);
                                                            }
                                                        }

                                                        // Simulate the final call to check for errors
                                                        const traceResult = await traceCall(
                                                            timelockTargetContract,
                                                            finalCall.name,
                                                            Array.from(finalCall.args),
                                                            timelockData,
                                                            statusText,
                                                            provider
                                                        );

                                                        statusText = traceResult.statusText;
                                                        setExecutionStatus(statusText);
                                                    }
                                                } catch (e) {
                                                    statusText += `\nCould not decode final function call`;
                                                    setExecutionStatus(statusText);
                                                }
                                            }

                                            // Calculate the operation ID
                                            try {
                                                const timelockInstance = new ethers.Contract(
                                                    nestedTargetContract.address,
                                                    nestedTargetContract.abi,
                                                    provider
                                                );

                                                const operationId = await timelockInstance.hashOperation(
                                                    nestedCall.args.target,
                                                    nestedCall.args.value,
                                                    nestedCall.args.data,
                                                    nestedCall.args.predecessor,
                                                    nestedCall.args.salt
                                                );

                                                statusText += `\n\nOperation ID: ${operationId}`;
                                                setExecutionStatus(statusText);

                                                // Check if this operation has been executed
                                                const isDone = await timelockInstance.isOperationDone(operationId);
                                                statusText += `\nOperation executed: ${isDone ? "Yes" : "No"}`;
                                                setExecutionStatus(statusText);

                                                // Save the relationship
                                                setRelatedTxs(prev => new Map(prev).set(txHash, operationId));

                                                // Add to tracked operations
                                                setTrackedOperations(prev => {
                                                    // Check if already tracked
                                                    if (prev.some(op => op.id === operationId)) {
                                                        return prev;
                                                    }

                                                    return [...prev, {
                                                        id: operationId,
                                                        target: nestedCall.args.target,
                                                        status: 'scheduled',
                                                        scheduledTx: txHash,
                                                        executionTime: Math.floor(Date.now() / 1000) + Number(delay)
                                                    }];
                                                });
                                            } catch (e) {
                                                logger.error("Error calculating operation ID:", e);
                                                statusText += `\nError calculating operation ID`;
                                                setExecutionStatus(statusText);
                                            }
                                        } else if (nestedCall.name === 'execute') {
                                            // This is executing a timelock operation
                                            statusText += `\nExecuting a timelock operation`;
                                            setExecutionStatus(statusText);

                                            try {
                                                // Calculate the operation ID
                                                const timelockInstance = new ethers.Contract(
                                                    nestedTargetContract.address,
                                                    nestedTargetContract.abi,
                                                    provider
                                                );

                                                const operationId = await timelockInstance.hashOperation(
                                                    nestedCall.args.target,
                                                    nestedCall.args.value,
                                                    nestedCall.args.data,
                                                    nestedCall.args.predecessor,
                                                    nestedCall.args.salt
                                                );

                                                statusText += `\nOperation ID: ${operationId} `;
                                                setExecutionStatus(statusText);

                                                // Save the relationship
                                                setRelatedTxs(prev => new Map(prev).set(txHash, operationId));

                                                // Update tracked operations
                                                setTrackedOperations(prev => prev.map(op =>
                                                    op.id === operationId
                                                        ? { ...op, status: 'executed', executedTx: txHash }
                                                        : op
                                                ));

                                                // Try to find the scheduling transaction
                                                const schedulingTx = Array.from(relatedTxs.entries())
                                                    .find(([_, opId]) => opId === operationId)?.[0];

                                                if (schedulingTx) {
                                                    statusText += `\nScheduling transaction: ${schedulingTx} `;
                                                    setExecutionStatus(statusText);
                                                }
                                            } catch (e) {
                                                logger.error("Error processing execute call:", e);
                                                statusText += `\nError processing execute call`;
                                                setExecutionStatus(statusText);
                                            }
                                        }
                                    }
                                }
                            } catch (e) {
                                logger.error("Error decoding nested call:", e);
                                statusText += `\nCould not decode nested call data`;
                                setExecutionStatus(statusText);
                            }
                        } else {
                            statusText += `\nTarget contract of nested call not found: ${target} `;
                            setExecutionStatus(statusText);
                        }
                    } else if (targetContract.name.toLowerCase().includes("timelock")) {
                        // Direct timelock call
                        if (decoded.name === 'execute') {
                            statusText += `\nDirect execution of timelock operation`;
                            setExecutionStatus(statusText);

                            // Analyze target and data
                            const target = decoded.args.target;
                            const data = decoded.args.data;

                            // Find target contract
                            const targetContract = contracts.find(c =>
                                c.address.toLowerCase() === target.toLowerCase()
                            );

                            if (targetContract) {
                                statusText += `\nExecuting on contract: ${targetContract.name} `;
                                setExecutionStatus(statusText);

                                // Try to decode the function call
                                try {
                                    const targetInterface = new ethers.utils.Interface(targetContract.abi);
                                    const targetCall = targetInterface.parseTransaction({ data });

                                    if (targetCall) {
                                        statusText += `\nCalling: ${targetContract.name}.${targetCall.name} `;
                                        setExecutionStatus(statusText);
                                    }
                                } catch (e) {
                                    statusText += `\nCould not decode target function call`;
                                    setExecutionStatus(statusText);
                                }
                            }
                        }
                    }
                }
            } catch (e) {
                logger.error("Error decoding transaction:", e);
                statusText += `\nCould not decode transaction data: ${(e as Error).message} `;
                setExecutionStatus(statusText);
            }

            // Look for relevant events in the transaction receipt
            if (receipt && receipt.logs.length > 0) {
                statusText += `\n\n${receipt.logs.length} events found in transaction`;
                setExecutionStatus(statusText);

                // Check each log against known contracts
                let eventCount = 0;
                for (const log of receipt.logs) {
                    const eventContract = contracts.find(c =>
                        c.address.toLowerCase() === log.address.toLowerCase()
                    );

                    if (eventContract) {
                        try {
                            const eventInterface = new ethers.utils.Interface(eventContract.abi);
                            const parsedLog = eventInterface.parseLog(log);

                            if (parsedLog) {
                                eventCount++;
                                statusText += `\nEvent ${eventCount}: ${eventContract.name}.${parsedLog.name} `;
                                setExecutionStatus(statusText);

                                // Special handling for Transfer events
                                if (parsedLog.name === 'Transfer') {
                                    const from = parsedLog.args.from;
                                    const to = parsedLog.args.to;
                                    const value = parsedLog.args.value || parsedLog.args.amount;

                                    if (from && to && value) {
                                        try {
                                            const formattedValue = ethers.utils.formatUnits(value, 18);
                                            statusText += `\nTransfer: ${from} -> ${to} (${formattedValue} tokens)`;
                                            setExecutionStatus(statusText);
                                        } catch (e) {
                                            statusText += `\nTransfer: ${from} -> ${to} (${value.toString()})`;
                                            setExecutionStatus(statusText);
                                        }
                                    }
                                }
                            }
                        } catch (e) {
                            // Skip events we can't parse
                        }
                    }
                }

                if (eventCount === 0) {
                    statusText += `\nNo recognizable events found`;
                    setExecutionStatus(statusText);
                }
            } else {
                statusText += `\nNo events found in transaction`;
                setExecutionStatus(statusText);
            }
        } catch (error) {
            logger.error("Error tracing transaction chain:", error);
            setExecutionStatus(`Error tracing transaction chain: ${error instanceof Error ? error.message : String(error)} `);
        }
    };

    // Similarly fix the trackTransactionChain function's status updates:
    // This completes the trackTransactionChain function
    const trackTransactionChain = async (
        transaction: ethers.providers.TransactionResponse,
        setStatus?: (status: string) => void,
        contractsList: { name: string; address: string; abi: any }[] = []
    ) => {
        try {
            // Check if this is a timelock schedule transaction
            if (await isTimelockSchedule(transaction)) {
                const operationHash = await getOperationHash(transaction.data || '0x');
                if (operationHash) {
                    setRelatedTxs(prev => new Map(prev).set(transaction.hash, operationHash));
                }
            }

            // First, handle the initial transaction using our existing method
            const receipt = await logBlockchainTransaction(transaction, setStatus);

            if (!receipt) return { receipt: {} as ethers.providers.TransactionReceipt, internalTransactions: [] };

            // Now analyze the receipt for internal transactions
            const internalTransactions = await analyzeTransactionReceipt(receipt, contractsList.length > 0 ? contractsList : contracts);

            // Update the status with information about related transactions
            if (setStatus && internalTransactions.length > 0) {
                const relatedOpHash = relatedTxs.get(transaction.hash);
                let statusMessage = `Transaction completed.Triggered ${internalTransactions.length} internal transaction${internalTransactions.length === 1 ? '' : 's'} `;
                if (relatedOpHash) {
                    statusMessage += `\nOperation Hash: ${relatedOpHash} `;
                }
                setStatus(statusMessage);
            }

            // Add internal transactions to our transaction history with relationship info
            for (const tx of internalTransactions) {
                setTransactions((prev: TrackedTransaction[]) => {
                    if (prev.some((existing: TrackedTransaction) => existing.hash === tx.hash)) return prev;

                    const newTx: TrackedTransaction = {
                        hash: tx.hash || `internal - ${Date.now()} `,
                        from: tx.from || receipt.from,
                        to: tx.to || '',
                        value: tx.value?.toString() || '0',
                        timestamp: Date.now(),
                        status: 'success' as 'success',
                        gasUsed: tx.gasUsed?.toString() || 'unknown',
                        description: tx.description || 'Internal transaction',
                        relatedOpHash: relatedTxs.get(transaction.hash) // Add relationship info
                    };

                    return [newTx, ...prev].slice(0, 10);
                });
            }

            return { receipt, internalTransactions };
        } catch (error) {
            logger.error("Error tracking transaction chain:", error);
            if (setStatus) {
                setStatus(`Error tracking transaction chain: ${error instanceof Error ? error.message : String(error)} `);
            }
            return { receipt: {} as ethers.providers.TransactionReceipt, internalTransactions: [] };
        }
    };

    // Function to track transaction from hash - this was missing in the original file
    const trackTransactionFromHash = async (txHash: string) => {
        if (!provider) return;

        try {
            setExecutionStatus(`Analyzing transaction ${txHash}...`);
            const tx = await provider.getTransaction(txHash);

            if (tx) {
                await trackTransactionChain(tx, setExecutionStatus, contracts);
                setExecutionStatus(`Transaction ${txHash} analyzed successfully`);
            } else {
                setExecutionStatus(`Transaction ${txHash} not found`);
            }
        } catch (error) {
            logger.error("Error analyzing transaction:", error);
            setExecutionStatus(`Error analyzing transaction: ${error instanceof Error ? error.message : String(error)} `);
        }
    };

    // Function to analyze transaction details
    const analyzeTransaction = async (txHash: string) => {
        if (!provider) return;

        try {
            const tx = await provider.getTransaction(txHash);
            if (!tx) {
                setExecutionStatus(`Transaction ${txHash} not found`);
                return;
            }

            const receipt = await provider.getTransactionReceipt(txHash);
            const decodedData = decodeTransactionData(tx.data, contracts);

            setAnalyzedTransaction({
                hash: tx.hash,
                from: tx.from,
                to: tx.to || '',
                value: tx.value.toString(),
                data: tx.data,
                gasLimit: tx.gasLimit.toString(),
                gasPrice: tx.gasPrice?.toString() || '',
                nonce: tx.nonce,
                decodedData,
                receipt: {
                    status: receipt?.status,
                    gasUsed: receipt?.gasUsed?.toString() || '',
                    logs: receipt?.logs || []
                }
            });
        } catch (error) {
            logger.error("Error analyzing transaction:", error);
            setExecutionStatus(`Error analyzing transaction: ${error instanceof Error ? error.message : String(error)} `);
        }
    };

    // Get transaction statistics
    const getTransactionStats = () => {
        const successful = transactions.filter(tx => tx.status === 'success').length;
        const failed = transactions.filter(tx => tx.status === 'error').length;
        const pending = transactions.filter(tx => tx.status === 'pending').length;

        return { total: transactions.length, successful, failed, pending };
    };

    const stats = getTransactionStats();

    // Render component
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Transaction History
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {executionStatus && <CircularProgress size={24} />}
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            // Function to reload transaction history from local storage or chain
                            const loadTransactionHistory = async () => {
                                try {
                                    setExecutionStatus("Loading transaction history...");

                                    // First try to load from localStorage
                                    const savedTxs = localStorage.getItem('memer_transactions');
                                    if (savedTxs) {
                                        setTransactions(JSON.parse(savedTxs));
                                        setExecutionStatus("Transaction history loaded from localStorage");
                                        return; // Exit early if we have transactions in localStorage
                                    }

                                    // Then try to get recent transactions if we have a provider
                                    if (provider && signer) {
                                        const address = await signer.getAddress();

                                        // Use a direct connection to the Anvil blockchain
                                        const jsonRpcProvider = new ethers.providers.JsonRpcProvider(
                                            'http://127.0.0.1:8545'
                                        );

                                        try {
                                            // Verify connection to Anvil
                                            const network = await jsonRpcProvider.getNetwork();
                                            logger.log("Connected to network:", network);

                                            // Fetch transaction history
                                            const filter = {
                                                fromBlock: 0,
                                                toBlock: 'latest',
                                                address: address
                                            };

                                            // Fetch transaction history using RPC method
                                            const logs = await jsonRpcProvider.getLogs(filter);
                                            logger.log(`Found ${logs.length} logs for address ${address}`);

                                            if (logs.length > 0) {
                                                // Process and add transactions
                                                const processedTxs = await Promise.all(
                                                    logs.slice(0, 10).map(async (log) => {
                                                        try {
                                                            const tx = await jsonRpcProvider.getTransaction(log.transactionHash);
                                                            const receipt = await jsonRpcProvider.getTransactionReceipt(log.transactionHash);

                                                            return {
                                                                hash: tx.hash,
                                                                from: tx.from,
                                                                to: tx.to || '',
                                                                value: tx.value.toString(),
                                                                timestamp: Date.now(),
                                                                status: receipt?.status === 1 ? 'success' : 'error',
                                                                gasUsed: receipt?.gasUsed?.toString() || '',
                                                            };
                                                        } catch (txError) {
                                                            logger.error("Error processing transaction:", txError);
                                                            return null;
                                                        }
                                                    })
                                                );

                                                // Filter out null entries and merge with existing transactions
                                                const validTxs = processedTxs.filter(tx => tx !== null);

                                                if (validTxs.length > 0) {
                                                    setTransactions(prev => {
                                                        const txMap = new Map(prev.map(tx => [tx.hash, tx]));
                                                        validTxs.forEach((tx) => {
                                                            if (tx) {
                                                                const trackedTx: TrackedTransaction = {
                                                                    ...tx,
                                                                    status: tx.status as 'success' | 'error' | 'pending'
                                                                };
                                                                txMap.set(trackedTx.hash, trackedTx);
                                                            }
                                                        });
                                                        return Array.from(txMap.values());
                                                    });

                                                    setExecutionStatus(`Loaded ${validTxs.length} transactions from blockchain`);
                                                } else {
                                                    setExecutionStatus("No valid transactions found on blockchain");
                                                }
                                            } else {
                                                setExecutionStatus("No transaction logs found for this address");
                                            }
                                        } catch (networkError) {
                                            logger.error("Network error:", networkError);
                                            setExecutionStatus("Could not connect to Anvil blockchain. Make sure it's running.");
                                        }
                                    } else {
                                        setExecutionStatus("No provider or signer available");
                                    }
                                } catch (error) {
                                    logger.error("Error loading transaction history:", error);
                                    setExecutionStatus(`Error loading history: ${error instanceof Error ? error.message : String(error)} `);
                                }
                            };

                            loadTransactionHistory();
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AnalyticsIcon />}
                        onClick={() => setAnalyzeDialogOpen(true)}
                    >
                        Analyze Transaction
                    </Button>
                </Box>
            </Box>

            {/* Summary Statistics */}
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <HistoryIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold">
                                {stats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Transactions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <SuccessIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {stats.successful}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Successful
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="error.main">
                                {stats.failed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Failed
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <PendingIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h4" fontWeight="bold" color="warning.main">
                                {stats.pending}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Pending
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid2>
            </Grid2>

            <Grid2 container spacing={3}>
                {/* Transaction Analysis Card */}
                {transactions.length === 0 && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Analyze Transaction
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="Transaction Hash"
                                placeholder="0x..."
                                value={transactionToAnalyze}
                                onChange={(e) => setTransactionToAnalyze(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                onClick={() => {
                                                    if (transactionToAnalyze && provider) {
                                                        trackTransactionFromHash(transactionToAnalyze);
                                                    }
                                                }}
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ mb: 2 }}
                            />

                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!transactionToAnalyze}
                                startIcon={<AnalyticsIcon />}
                                onClick={() => {
                                    if (transactionToAnalyze && provider) {
                                        trackTransactionFromHash(transactionToAnalyze);
                                    }
                                }}
                            >
                                Analyze Transaction
                            </Button>
                        </Paper>
                    </Grid2>
                )}

                {/* Transaction Chain Explorer */}
                <Grid2 size={{ xs: 12, md: transactions.length === 0 ? 6 : 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TimelineIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Transaction Chain Explorer
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enter a MultiSig transaction hash to trace the complete chain of execution through the timelock to the final token transfer.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Transaction Hash"
                                value={transactionToAnalyze}
                                onChange={(e) => setTransactionToAnalyze(e.target.value)}
                                placeholder="0x..."
                            />
                            <Button
                                variant="contained"
                                onClick={() => traceTransactionChain(transactionToAnalyze)}
                                disabled={!transactionToAnalyze || !provider}
                                startIcon={<TimelineIcon />}
                            >
                                Trace Chain
                            </Button>
                        </Box>

                        {/* Display the execution status as a pre-formatted block */}
                        {executionStatus && (
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    borderRadius: 1,
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    maxHeight: '400px',
                                    overflow: 'auto'
                                }}
                            >
                                {executionStatus}
                            </Box>
                        )}
                    </Paper>
                </Grid2>

                {/* Transaction History Display */}
                <Grid2 size={{ xs: 12 }}>
                    <EnhancedTransactionHistory
                        transactions={transactions}
                        contracts={contracts}
                    />
                </Grid2>
            </Grid2>

            {/* Transaction Analysis Dialog */}
            <Dialog
                open={analyzeDialogOpen}
                onClose={() => setAnalyzeDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Analyze Transaction</Typography>
                        <IconButton onClick={() => setAnalyzeDialogOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            label="Transaction Hash"
                            placeholder="0x..."
                            value={transactionToAnalyze}
                            onChange={(e) => setTransactionToAnalyze(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!transactionToAnalyze}
                            onClick={() => {
                                if (transactionToAnalyze && provider) {
                                    analyzeTransaction(transactionToAnalyze);
                                }
                            }}
                        >
                            Analyze
                        </Button>

                        {analyzedTransaction && (
                            <SecureDataViewer
                                data={analyzedTransaction.data || ''}
                                title="Transaction Data"
                                contracts={contracts}
                            />
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TransactionHistory;
